import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Amplify } from 'aws-amplify';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  type SignUpInput,
  type ConfirmSignUpInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from '@aws-amplify/auth';
import {
  hasCognitoEnvConfig,
  awsConfigFromEnv,
  fetchCognitoConfig,
  buildAmplifyConfig,
} from '../aws-config';
import { User, authApi } from '../services/api';

// Configure Amplify immediately when we have env config, so it's ready before any auth call
if (hasCognitoEnvConfig()) {
  Amplify.configure(awsConfigFromEnv);
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
}

interface AuthContextValue extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  signup: (data: {
    email: string;
    password: string;
    full_name?: string;
    state?: string;
  }) => Promise<void>;
  confirmSignup: (username: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPasswordWithCode: (email: string, code: string, newPassword: string) => Promise<void>;
  refreshAuth: () => Promise<void>;
  clearError: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [amplifyConfigured, setAmplifyConfigured] = useState(hasCognitoEnvConfig());
  const amplifyConfiguredRef = useRef(hasCognitoEnvConfig());

  const clearError = useCallback(() => setError(null), []);

  const refreshAuth = useCallback(async () => {
    if (!amplifyConfiguredRef.current) return;
    try {
      setLoading(true);

      const session = await fetchAuthSession();

      if (!session.tokens) {
        setUser(null);
        setIsAdmin(false);
        return;
      }

      const userDetails = await authApi.me();
      setUser(userDetails);
      setIsAdmin(userDetails.is_admin ?? false);
    } catch (err: any) {
      // Session invalid (e.g. 400 from Cognito), expired, or revoked – clear Amplify state so next sign-in works
      const msg = err?.message ?? err?.name ?? String(err);
      if (process.env.NODE_ENV === 'development') {
        console.warn('Auth refresh failed (clearing session):', msg, err);
      }
      try {
        await signOut();
      } catch {
        // ignore signOut errors
      }
      setUser(null);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load Cognito config and configure Amplify before any auth operations.
  // Uses VITE_* env if set, otherwise fetches from backend (e.g. when only root .env has COGNITO_*).
  useEffect(() => {
    let cancelled = false;

    async function initAuth() {
      try {
        let config;
        if (hasCognitoEnvConfig()) {
          config = {
            region: import.meta.env.VITE_COGNITO_REGION || 'us-east-1',
            userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
            userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
          };
        } else {
          config = await fetchCognitoConfig();
        }
        if (cancelled) return;
        if (!config.userPoolId || !config.userPoolClientId) {
          setError('Auth UserPool not configured. Set COGNITO_* in .env and VITE_COGNITO_* in frontend/.env, or ensure backend /api/auth/cognito-config returns them.');
          setLoading(false);
          return;
        }
        Amplify.configure(buildAmplifyConfig(config));
        amplifyConfiguredRef.current = true;
        setAmplifyConfigured(true);
        await refreshAuth();
      } catch (err: any) {
        if (cancelled) return;
        console.error('Auth init error:', err);
        setError(err.message || 'Failed to load auth configuration. Is the backend running?');
        setLoading(false);
      }
    }

    initAuth();
    return () => {
      cancelled = true;
    };
  }, [refreshAuth]);

  // Keep ref in sync for env-config case (configured at module load)
  useEffect(() => {
    amplifyConfiguredRef.current = amplifyConfigured;
  }, [amplifyConfigured]);

  const login = useCallback(
    async (username: string, password: string) => {
      if (!amplifyConfigured) {
        const msg = 'Auth is not ready yet. Wait for the app to load or check that Cognito is configured.';
        setError(msg);
        throw new Error(msg);
      }
      setError(null);
      const signInOptions = { options: { authFlowType: 'USER_PASSWORD_AUTH' as const } };
      try {
        await signIn({ username, password, ...signInOptions });
        await refreshAuth();
      } catch (err: any) {
        // After confirmSignUp, Cognito may already have a session; sign out and retry once
        const isAlreadySignedIn =
          err?.message?.toLowerCase().includes('already a signed in user') ||
          err?.message?.toLowerCase().includes('already signed in');
        if (isAlreadySignedIn) {
          try {
            await signOut();
            await signIn({ username, password, ...signInOptions });
            await refreshAuth();
            return;
          } catch (retryErr: any) {
            setError(retryErr.message || 'Login failed');
            throw new Error(retryErr.message || 'Login failed');
          }
        }
        const message = err.message || 'Login failed';
        setError(message);
        throw new Error(message);
      }
    },
    [refreshAuth, amplifyConfigured]
  );

  const signup = useCallback(
    async (data: {
      email: string;
      password: string;
      full_name?: string;
      state?: string;
    }) => {
      if (!amplifyConfigured) {
        const msg = 'Auth is not ready yet. Wait for the app to load or check that Cognito is configured.';
        setError(msg);
        throw new Error(msg);
      }
      setError(null);
      try {
        const signUpInput: SignUpInput = {
          username: data.email, // Use email as username since Cognito is configured with username_attributes = ["email"]
          password: data.password,
          options: {
            userAttributes: {
              email: data.email,
              ...(data.full_name && { name: data.full_name }),
            },
          },
        };

        await signUp(signUpInput);
        
        // User needs to confirm email before they can log in
        // Don't set user yet - wait for confirmation
      } catch (err: any) {
        const message = err.message || 'Signup failed';
        setError(message);
        throw new Error(message);
      }
    },
    [amplifyConfigured]
  );

  const confirmSignup = useCallback(
    async (username: string, code: string) => {
      if (!amplifyConfigured) {
        const msg = 'Auth is not ready yet.';
        setError(msg);
        throw new Error(msg);
      }
      setError(null);
      try {
        const confirmInput: ConfirmSignUpInput = {
          username,
          confirmationCode: code,
        };
        
        await confirmSignUp(confirmInput);
        
        // User is now confirmed and can log in
      } catch (err: any) {
        const message = err.message || 'Confirmation failed';
        setError(message);
        throw new Error(message);
      }
    },
    [amplifyConfigured]
  );

  const logout = useCallback(async () => {
    try {
      await signOut();
      setUser(null);
      setIsAdmin(false);
    } catch (err) {
      console.error('Logout error:', err);
      setUser(null);
      setIsAdmin(false);
    }
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    if (!amplifyConfigured) {
      setError('Auth is not ready yet.');
      throw new Error('Auth is not ready yet.');
    }
    setError(null);
    try {
      const resetInput: ResetPasswordInput = {
        username: email,
      };
      
      await resetPassword(resetInput);
      
      // Cognito will send a code to the user's email
    } catch (err: any) {
      const message = err.message || 'Password reset request failed';
      setError(message);
      throw new Error(message);
    }
  }, [amplifyConfigured]);

  const resetPasswordWithCode = useCallback(
    async (email: string, code: string, newPassword: string) => {
      if (!amplifyConfigured) {
        setError('Auth is not ready yet.');
        throw new Error('Auth is not ready yet.');
      }
      setError(null);
      try {
        const confirmInput: ConfirmResetPasswordInput = {
          username: email,
          confirmationCode: code,
          newPassword,
        };
        
        await confirmResetPassword(confirmInput);
        
        // Password has been reset, user can now log in
      } catch (err: any) {
        const message = err.message || 'Password reset failed';
        setError(message);
        throw new Error(message);
      }
    },
    [amplifyConfigured]
  );

  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAdmin,
    login,
    signup,
    confirmSignup,
    logout,
    forgotPassword,
    resetPasswordWithCode,
    refreshAuth,
    clearError,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
